import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { resetPasswordSchema } from '@fp3/shared'
import { Link, useSearchParams } from 'react-router-dom'
import { useResetPassword } from '../hooks/useResetPassword'
import { getErrorMessage } from '@/lib/utils/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const resetFormSchema = resetPasswordSchema
  .pick({ password: true })
  .extend({ confirmPassword: z.string() })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetFormData = z.infer<typeof resetFormSchema>

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetFormSchema),
    mode: 'onChange',
  })

  const { mutate: resetPassword, isPending, isSuccess, error } = useResetPassword()

  const onSubmit = (data: ResetFormData) => {
    if (!token) return
    resetPassword({ token, password: data.password })
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">Invalid reset link. No token provided.</p>
            <Link
              to="/forgot-password"
              className="text-primary hover:underline font-medium text-sm"
            >
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-4">
          <div className="flex justify-center">
            <img src="/vectorFP3Logo.svg" alt="FP3 Finance" className="h-24" />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Password reset</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-success mb-3">
                Your password has been reset successfully.
              </p>
              <Link to="/login" className="text-primary hover:underline font-medium text-sm">
                Continue to login
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-4">
        <div className="flex justify-center">
          <img src="/vectorFP3Logo.svg" alt="FP3 Finance" className="h-24" />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Set new password</CardTitle>
            <CardDescription>Enter your new password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && (
                <div
                  className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
                  role="alert"
                >
                  <p>{getErrorMessage(error)}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  aria-invalid={!!errors.password}
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Password must be at least 8 characters with a number and special character
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  aria-invalid={!!errors.confirmPassword}
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isPending || !isDirty || !isValid}>
                {isPending ? 'Resetting...' : 'Reset password'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline font-medium">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  )
}
